/**
 * Два варианта КП для МСК Газобетона — данные для единого окна формирования.
 *
 * Новых расчётов здесь нет. Оба варианта собираются теми же функциями, что и раньше
 * стояли за кнопками «КП — полная загрузка шаланд» и «КП — весь объём»:
 *   - состав и суммы — buildGazCommercialProposalBlocks поверх
 *     buildGazCommercialProposalSendPayload;
 *   - машины — buildGazVehicleLoadModel;
 *   - наличное округление — resolveGazCashTotal из gazCashRounding, формула не
 *     дублируется.
 *
 * Вариант «только полностью загруженные машины» показывается не всегда: если весь
 * заказ и так состоит из полных машин, либо полных машин нет вовсе, выбирать не из
 * чего — второй вариант в окно не попадает.
 */

import buildGazCommercialProposalBlocks, {
  buildGazCommercialProposalSendPayload,
} from './buildGazCommercialProposalBlocks.js';
import buildGazVehicleLoadModel, { buildVehicleTypesText } from './buildGazVehicleLoadModel.js';
import { resolveGazCashTotal, buildGazCashRoundingNote } from './gazCashRounding.js';
import { vehiclesWord } from '../../utils/gazDeclensions.js';
import { formatNumber } from '../../utils/gazTextFormat.js';

export const GAZ_SEND_MODE_FULL = 'full';
export const GAZ_SEND_MODE_PARTIAL = 'partial';

export const GAZ_SEND_MODE_LABELS = {
  [GAZ_SEND_MODE_FULL]: 'Весь заказ',
  [GAZ_SEND_MODE_PARTIAL]: 'Только полностью загруженные машины',
};

/**
 * Название варианта одно при любом числе машин: важно не количество, а то, что
 * заказ уедет не целиком.
 */
export const getPartialModeLabel = () => GAZ_SEND_MODE_LABELS[GAZ_SEND_MODE_PARTIAL];

const round3 = (value) => Math.round((Number(value) || 0) * 1000) / 1000;

// Ноль и отрицательные значения здесь по-прежнему считаются «нечего показывать»,
// сама запись числа — общим форматтером.
const numberText = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num) || num <= 0) {
    return null;
  }
  return formatNumber(round3(num));
};

/** Объём газобетона варианта: складываются количества уже собранных строк материалов. */
const volumeOf = (blocks) => {
  let volume = 0;
  for (const block of (Array.isArray(blocks) ? blocks : [])) {
    for (const row of (Array.isArray(block?.rows) ? block.rows : [])) {
      if (row?.kind === 'material' && String(row?.unit || '').trim().toLowerCase() === 'м3') {
        volume += Number(row.quantity) || 0;
      }
    }
  }
  return round3(volume);
};

const totalOf = (blocks) => (Array.isArray(blocks) ? blocks : [])
  .reduce((sum, block) => sum + (Number(block?.total) || 0), 0);

/**
 * Короткая подпись про транспорт: «2 машины», «1 шаланда».
 * Тип называется, только когда машина одна — иначе перечисление длиннее пользы.
 */
const vehiclesTextOf = (model) => {
  const vehicles = Array.isArray(model?.vehicles) ? model.vehicles : [];
  const total = Number(model?.totalVehicles) || 0;
  if (total <= 0) {
    return null;
  }
  if (total === 1 && vehicles.length === 1) {
    return `1 ${String(vehicles[0].type || '').toLowerCase() || vehiclesWord(1)}`;
  }
  return `${total} ${vehiclesWord(total)}`;
};

const buildOption = ({ mode, offer, paymentMethod }) => {
  const payload = buildGazCommercialProposalSendPayload(offer, mode, { isMskGazobetonScope: true });
  const proposal = buildGazCommercialProposalBlocks(payload, { isMskGazobetonScope: true });
  const blocks = proposal.blocks || [];

  if (!blocks.some((block) => (block?.rows || []).length > 0)) {
    return null;
  }

  const sourceTotal = totalOf(blocks);
  // Каждый вариант округляется отдельно и той же функцией, что и итог на экране.
  const cash = resolveGazCashTotal({ total: sourceTotal, isMskGazobetonScope: true, paymentMethod });

  const model = buildGazVehicleLoadModel(payload, blocks);
  const vehiclesCount = Number(model?.totalVehicles) || 0;
  // Типы транспорта берутся из готовой модели машин, а не собираются заново из текста.
  const vehicleTypes = buildVehicleTypesText(model);

  return {
    mode,
    label: GAZ_SEND_MODE_LABELS[mode],
    payload,
    blocks,
    // Машины варианта отдаются наружу как есть: карточка выбора рисует их у себя,
    // а не строит вторую модель по тем же данным.
    vehicleModel: model,
    volume: volumeOf(blocks),
    vehiclesCount,
    vehiclesText: vehiclesTextOf(model),
    vehicleTypes,
    sourceTotal,
    total: cash.applies ? cash.total : sourceTotal,
    roundingApplies: cash.applies,
    roundingDifference: cash.difference,
    roundingNote: buildGazCashRoundingNote(cash),
  };
};

/** «Весь заказ — 50,625 м³ · 2 машины · 285 000 ₽» */
export const buildGazSendOptionSummary = (option) => {
  if (!option) {
    return '';
  }
  const parts = [];
  const volume = numberText(option.volume);
  if (volume) {
    parts.push(`${volume} м³`);
  }
  if (option.vehiclesText) {
    parts.push(option.vehiclesText);
  }
  parts.push(`${Math.ceil(Number(option.total) || 0).toLocaleString('ru-RU')} ₽`);
  return parts.join(' · ');
};

/**
 * @param {object}  params
 * @param {object}  params.offer                — выбранное предложение целиком
 * @param {string}  params.paymentMethod        — форма оплаты
 * @param {boolean} params.isMskGazobetonScope  — признак области, приходит готовым
 * @returns {Array} варианты в порядке показа; первый — выбранный по умолчанию.
 */
export const buildGazSendOptions = ({ offer, paymentMethod, isMskGazobetonScope } = {}) => {
  if (!isMskGazobetonScope || !offer) {
    return [];
  }

  const built = buildOption({ mode: GAZ_SEND_MODE_FULL, offer, paymentMethod });
  if (!built) {
    return [];
  }

  // Рекомендация системы, а не выбор менеджера: весь заказ закрывает потребность
  // клиента целиком, поэтому предлагается по умолчанию. Менеджер может выбрать
  // неполную доставку осознанно — метка от этого не переезжает.
  const whole = { ...built, isRecommended: true };

  const proposal = buildGazCommercialProposalBlocks(offer, { isMskGazobetonScope: true });
  const mainBlock = (proposal.blocks || []).find((block) => block.key === 'main');
  const secondaryBlock = (proposal.blocks || []).find((block) => block.key === 'secondary');

  const hasFullVehicles = (mainBlock?.rows || []).some((row) => row.kind === 'material');
  const hasRemainder = (secondaryBlock?.rows || []).length > 0;

  // Выбирать не из чего: либо весь заказ и так едет полными машинами, либо полных
  // машин нет вовсе. В обоих случаях второй вариант — копия первого или пустышка.
  if (!hasFullVehicles || !hasRemainder) {
    // Признак читает верхняя сводка: цена за м³ при полной погрузке считается по
    // варианту полных машин, а когда остатка нет — им и является весь заказ.
    // Полных машин нет вовсе — помечать нечего.
    return [hasFullVehicles ? { ...whole, isFullVehiclesOnly: true } : whole];
  }

  const fullVehiclesOnly = buildOption({ mode: GAZ_SEND_MODE_PARTIAL, offer, paymentMethod });
  if (!fullVehiclesOnly) {
    return [whole];
  }

  // Разница между уже посчитанными итогами вариантов — своего расчёта нет.
  const savings = Math.max(Math.ceil(whole.total) - Math.ceil(fullVehiclesOnly.total), 0);
  // Сколько клиент недополучит: разница объёмов тех же двух вариантов.
  const shortfall = round3(Math.max(whole.volume - fullVehiclesOnly.volume, 0));

  // Порядок показа: сначала «Только полностью загруженные машины», потом «Весь
  // заказ». Первым идёт вариант, который менеджер предлагает клиенту как выгодный;
  // полный заказ читается вторым, как то, что получится, если везти всё.
  //
  // Выбранным по умолчанию остаётся «Весь заказ» — он задан состоянием карточки,
  // а не позицией в списке.
  return [{
    ...fullVehiclesOnly,
    savings: savings > 0 ? savings : null,
    shortfall: shortfall > 0 ? shortfall : null,
    isPartialOrder: true,
    // Этот вариант и есть погрузка полными машинами — по нему считается цена за м³
    // при полной погрузке.
    isFullVehiclesOnly: true,
  }, whole];
};

export default buildGazSendOptions;
